import { api } from '../api/api';
import { clearImpersonationEmail } from './impersonation';

// ============ Types ============

export interface SignInParams {
  email: string;
  password: string;
}

export interface SignInResponse {
  success: boolean;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  challengeName?: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP' | 'NEW_PASSWORD_REQUIRED';
  session?: string;
  mfaSetup?: {
    secretCode: string;
    qrCodeUri: string;
  };
}

export interface VerifyMfaParams {
  code: string;
  session: string;
  challengeType: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';
  email: string;
}

export interface NewPasswordParams {
  email: string;
  newPassword: string;
  session: string;
  firstName?: string;
  lastName?: string;
}

export interface MfaSetupResponse {
  success: boolean;
  secretCode?: string;
  qrCodeUri?: string;
  session?: string;
}

export interface SetupMfaParams {
  code: string;
  session: string;
  email: string;
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  deliveryMedium?: string;
  destination?: string;
}

export interface ConfirmForgotPasswordParams {
  email: string;
  code: string;
  newPassword: string;
}

export interface ChangePasswordParams {
  oldPassword: string;
  newPassword: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarUrl?: string;
  preferredLanguage?: string;
  timezone?: string;
  birthDate?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  isEmailVerified?: boolean;
  lastLoginAt?: string;
  /** Identity provider: 'cognito' (native email/password) or an SSO code
   *  ('google' | 'facebook' | 'apple' | 'amazon'). SSO accounts cannot reset a
   *  Cognito password. */
  authProvider?: string;
  /** The organization this user belongs to (from user.organizationId on the backend). */
  organization?: {
    id: number;
    name: string;
    slug: string;
    /** False until onboarding configures the placeholder org created at sign-up. */
    setupCompleted?: boolean;
    /** Branding (T-1301): shown in the recipe viewer, cook mode and printed labels. */
    logoUrl?: string | null;
    brandPrimaryColor?: string | null;
    brandSecondaryColor?: string | null;
  };
  /** @deprecated backend now sends `organization`, not `company`. Use `organization` instead. */
  company?: {
    id: string;
    name: string;
    slug: string;
  };
  permissions: string[];
  scopes: { type: string; id: number | string }[];
  roles: { code: string; name: string; scopeType?: string; scopeId?: string }[];
  isImpersonating?: boolean;
  impersonationActor?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    roleName?: string | null;
    organizationName?: string | null;
  };
}

export interface MfaStatus {
  enabled: boolean;
  preferred: string | null;
}

// ============ Token Storage ============

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'yeschef_access_token',
  ID_TOKEN: 'yeschef_id_token',
  REFRESH_TOKEN: 'yeschef_refresh_token',
  EXPIRES_AT: 'yeschef_expires_at',
};

export function storeTokens(response: SignInResponse): void {
  if (response.accessToken) {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.accessToken);
  }
  if (response.idToken) {
    localStorage.setItem(TOKEN_KEYS.ID_TOKEN, response.idToken);
  }
  if (response.refreshToken) {
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, response.refreshToken);
  }
  if (response.expiresIn) {
    const expiresAt = Date.now() + response.expiresIn * 1000;
    localStorage.setItem(TOKEN_KEYS.EXPIRES_AT, expiresAt.toString());
  }
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.EXPIRES_AT);
  clearImpersonationEmail();
}

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
}

export function getIdToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
}

export function isTokenExpired(): boolean {
  const expiresAt = localStorage.getItem(TOKEN_KEYS.EXPIRES_AT);
  if (!expiresAt) return true;
  // Add 60 second buffer
  return Date.now() > parseInt(expiresAt) - 60000;
}

// ============ Auth Session ============

export interface AuthSession {
  tokens?: {
    accessToken?: string;
    idToken?: {
      toString(): string;
    };
    refreshToken?: string;
  };
}

export async function getAuthSession(): Promise<AuthSession | null> {
  const accessToken = getAccessToken();
  const idToken = getIdToken();
  const refreshToken = getRefreshToken();

  if (!accessToken || !idToken) {
    return null;
  }

  // Check if token is expired and refresh if needed
  if (isTokenExpired() && refreshToken) {
    try {
      await refreshTokens();
    } catch {
      clearTokens();
      return null;
    }
  }

  return {
    tokens: {
      accessToken: getAccessToken() || undefined,
      idToken: {
        toString: () => getIdToken() || '',
      },
      refreshToken: getRefreshToken() || undefined,
    },
  };
}

// ============ Auth API Functions ============

export interface SignUpParams {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password: string;
}

export interface SignUpResponse {
  success: boolean;
  message?: string;
}

/**
 * Register a new user account
 */
export async function signUpUser(params: SignUpParams): Promise<SignUpResponse> {
  return api.post<SignUpResponse>('/auth/signup', params, { skipAuth: true });
}

export interface ConfirmSignUpParams {
  email: string;
  code: string;
}

/**
 * Confirm email with verification code
 */
export async function confirmSignUp(params: ConfirmSignUpParams): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>('/auth/confirm-signup', params, { skipAuth: true });
}

/**
 * Resend email verification code
 */
export async function resendConfirmationCode(email: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>('/auth/resend-code', { email }, { skipAuth: true });
}

/**
 * Sign in with email and password
 */
export async function signInUser(params: SignInParams): Promise<SignInResponse> {
  const response = await api.post<SignInResponse>('/auth/signin', params, { skipAuth: true });
  
  if (response.success && response.accessToken) {
    storeTokens(response);
  }
  
  return response;
}

/**
 * Verify MFA code during sign-in
 */
export async function verifyMfa(params: VerifyMfaParams): Promise<SignInResponse> {
  const response = await api.post<SignInResponse>('/auth/verify-mfa', params, { skipAuth: true });
  
  if (response.success && response.accessToken) {
    storeTokens(response);
  }
  
  return response;
}

/**
 * Complete new password challenge
 */
export async function completeNewPassword(params: NewPasswordParams): Promise<SignInResponse> {
  const response = await api.post<SignInResponse>('/auth/complete-new-password', params, { skipAuth: true });
  
  if (response.success && response.accessToken) {
    storeTokens(response);
  }
  
  return response;
}

/**
 * Initiate MFA setup
 */
export async function initiateMfaSetup(): Promise<MfaSetupResponse> {
  return api.post<MfaSetupResponse>('/auth/mfa/setup');
}

/**
 * Complete MFA setup
 */
export async function completeMfaSetup(params: SetupMfaParams): Promise<SignInResponse> {
  const response = await api.post<SignInResponse>('/auth/mfa/complete-setup', params, { skipAuth: true });
  
  if (response.success && response.accessToken) {
    storeTokens(response);
  }
  
  return response;
}

/**
 * Enable MFA for the account
 */
export async function enableMfa(): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>('/auth/mfa/enable');
}

/**
 * Disable MFA for the account
 */
export async function disableMfa(): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>('/auth/mfa/disable');
}

/**
 * Get MFA status
 */
export async function getMfaStatus(): Promise<MfaStatus> {
  return api.get<MfaStatus>('/auth/mfa/status');
}

/**
 * Request password reset
 */
export async function forgotPassword(params: ForgotPasswordParams): Promise<ForgotPasswordResponse> {
  return api.post<ForgotPasswordResponse>('/auth/forgot-password', params, { skipAuth: true });
}

/**
 * Confirm password reset with code
 */
export async function confirmForgotPassword(params: ConfirmForgotPasswordParams): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>('/auth/confirm-forgot-password', params, { skipAuth: true });
}

/**
 * Change password for authenticated user
 */
export async function changePassword(params: ChangePasswordParams): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>('/auth/change-password', params);
}

/**
 * Refresh access tokens
 */
export async function refreshTokens(): Promise<SignInResponse> {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await api.post<SignInResponse>(
    '/auth/refresh',
    { refreshToken },
    { skipAuth: true }
  );

  if (response.success && response.accessToken) {
    storeTokens(response);
  }

  return response;
}

/**
 * Sign out user
 */
export async function signOutUser(globalSignOut: boolean = false): Promise<{ success: boolean }> {
  try {
    await api.post<{ success: boolean }>('/auth/signout', { globalSignOut });
  } catch {
    // Even if API call fails, clear local tokens
  }
  
  clearTokens();
  return { success: true };
}

export async function switchOrganization(
  organizationId: number | null,
): Promise<{ id: number; name: string; slug: string } | null> {
  // null clears the org context → super admin views all organizations.
  return api.patch<{ id: number; name: string; slug: string } | null>('/auth/me/organization', { organizationId });
}

/**
 * Permanently delete the current user's own account. The backend soft-deletes
 * the record and removes the Cognito user so the email can be registered again.
 * Local tokens are cleared on success since the session is no longer valid.
 */
export async function deleteOwnAccount(): Promise<{ success: boolean }> {
  const result = await api.delete<{ success: boolean }>('/auth/me');
  clearTokens();
  return result;
}

export interface ActiveSession {
  id: number;
  device: string;
  location: string | null;
  ipAddress: string | null;
  lastSeenAt: string;
  isCurrent: boolean;
}

/** List the current user's active sessions (recorded device/IP/geo + last seen). */
export async function getSessions(): Promise<ActiveSession[]> {
  return api.get<ActiveSession[]>('/auth/sessions');
}

export interface LoginHistoryEntry {
  id: number;
  device: string;
  location: string | null;
  ipAddress: string | null;
  createdAt: string;
}

/** Recent sign-ins for the current user (newest first). */
export async function getLoginHistory(): Promise<LoginHistoryEntry[]> {
  return api.get<LoginHistoryEntry[]>('/auth/sessions/history');
}

/**
 * Sign out of all sessions. Cognito only supports a global sign-out, so this
 * ends every session (including the current device); local tokens are cleared.
 */
export async function signOutAllSessions(): Promise<{ success: boolean }> {
  const result = await api.post<{ success: boolean }>('/auth/sessions/sign-out');
  clearTokens();
  return result;
}

/**
 * Get current authenticated user
 */
export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  const session = await getAuthSession();
  
  if (!session?.tokens?.accessToken) {
    return null;
  }

  try {
    return await api.post<AuthUser>('/auth/me', undefined, { skipToast: true });
  } catch {
    return null;
  }
}

/**
 * Get user permissions
 */
export async function getUserPermissions(): Promise<{
  permissions: string[];
  scopes: { type: string; id: string }[];
  roles: { code: string; name: string; scopeType?: string; scopeId?: string }[];
}> {
  return api.get('/auth/permissions');
}
